import React from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Stack, Paper, Chip,
  TextField, Select, MenuItem, IconButton, Table, TableHead, TableRow,
  TableCell, TableBody, Tooltip, Autocomplete, Checkbox, Divider,
  Skeleton, LinearProgress, ToggleButtonGroup, ToggleButton, FormControlLabel,
  Radio, RadioGroup,
} from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DoneRounded from '@mui/icons-material/DoneRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import FilterListRounded from '@mui/icons-material/FilterListRounded';
import HubOutlined from '@mui/icons-material/HubOutlined';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import { buildApiUrl } from '../config';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { ConnectorIcon, prettyConnectorType } from '../utils/connectorMeta';
import { useWarehouseLabels, resolveLabel, WarehouseLabelMap } from '../hooks/useWarehouseLabels';
import { formatRelativeTime } from '../utils/date';
import { joinNames, useDataModelAccess } from './data-model-access/useDataModelAccess';
import { VisibleToPicker } from './data-model-access/VisibleToPicker';

// ── Helpers ──────────────────────────────────────────────────────────────────
function getFirstConfidenceValue(conf: any): number | null {
  const extract = (v: any): number | null => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
    if (v && typeof v === 'object') {
      if (typeof v.value === 'number') return v.value;
      if (typeof v.score === 'number') return v.score;
      if (typeof v.confidence === 'number') return v.confidence;
    }
    return null;
  };
  if (Array.isArray(conf)) {
    for (const item of conf) { const n = extract(item); if (n !== null) return n; }
    return null;
  }
  return extract(conf);
}

function getFieldConfidence(field: any): number | null {
  const mappings = Array.isArray(field?.mappings) ? field.mappings : [];
  if (mappings.length > 0) {
    const v = getFirstConfidenceValue(mappings[0]?.confidence);
    if (typeof v === 'number') return v;
  }
  return getFirstConfidenceValue(field?.confidence);
}

function fmtPct(conf: number | null): string {
  if (conf === null || typeof conf !== 'number') return '—';
  return `${Math.round(conf * 100)}%`;
}

// Tag semantics (kept in lockstep with the data-model page so chips read the
// same way wherever they appear).
type TagTone = 'pii' | 'core' | 'transactional' | 'reference' | 'unassigned' | 'neutral';
function classifyTag(raw: string): TagTone {
  const t = String(raw || '').toLowerCase().trim();
  if (!t) return 'neutral';
  if (t === 'pii' || t === 'sensitive' || t === 'phi') return 'pii';
  if (t === 'core' || t === 'primary') return 'core';
  if (t === 'transactional' || t === 'event' || t === 'fact') return 'transactional';
  if (t === 'reference' || t === 'lookup' || t === 'dimension') return 'reference';
  if (t === 'unassigned') return 'unassigned';
  return 'neutral';
}
function tagChipProps(tone: TagTone): {
  color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';
  variant: 'filled' | 'outlined';
  icon?: React.ReactElement;
} {
  switch (tone) {
    case 'pii': return { color: 'warning', variant: 'filled', icon: <ShieldRounded sx={{ fontSize: 12 }} /> };
    case 'core': return { color: 'primary', variant: 'filled' };
    case 'transactional': return { color: 'info', variant: 'filled' };
    case 'reference': return { color: 'default', variant: 'outlined' };
    case 'unassigned': return { color: 'warning', variant: 'outlined' };
    default: return { color: 'default', variant: 'outlined' };
  }
}

// Deterministic letter glyph color from entity name. Two entities that start
// with the same letter shouldn't be visually identical.
function glyphPaletteFor(name: string): { bg: string; fg: string } {
  const palette = [
    { bg: '#5B6CFF', fg: '#fff' },
    { bg: '#2BB673', fg: '#fff' },
    { bg: '#E07A5F', fg: '#fff' },
    { bg: '#7C5CFF', fg: '#fff' },
    { bg: '#0EA5E9', fg: '#fff' },
    { bg: '#F59E0B', fg: '#1F2937' },
    { bg: '#EC4899', fg: '#fff' },
    { bg: '#14B8A6', fg: '#fff' },
  ];
  let hash = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

// Confidence tone — green high, amber mid, red low.
function confTone(c: number | null): 'success' | 'warning' | 'error' | 'muted' {
  if (c === null || typeof c !== 'number') return 'muted';
  if (c >= 0.8) return 'success';
  if (c >= 0.5) return 'warning';
  return 'error';
}

// ── Letter glyph avatar ─────────────────────────────────────────────────────
function EntityAvatar({ name, size = 40 }: { name: string; size?: number }) {
  const { bg, fg } = glyphPaletteFor(name);
  const letter = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const fontSize = Math.round(size * 0.45);
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: 1.25,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bg,
        color: fg,
        fontWeight: 700,
        fontSize,
        letterSpacing: 0.5,
        lineHeight: 1,
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      }}
    >
      {letter}
    </Box>
  );
}

// Shared row of connector brand icons + overflow with rich tooltip. Used at
// the entity-level (page hero) and at the field-level (table row).
function SourceIcons({
  connectors,
  size = 16,
  max = 4,
}: {
  // Each entry already resolved to brand metadata.
  connectors: Array<{ connectorId: string; type: string; name: string; rows?: string[] }>;
  size?: number;
  max?: number;
}) {
  const theme = useTheme();
  if (!connectors.length) {
    return <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>—</Typography>;
  }
  const visible = connectors.slice(0, max);
  const overflow = connectors.length - visible.length;
  const tooltip = (
    <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
      {connectors.map((c, i) => (
        <Box key={i} sx={{ mb: i === connectors.length - 1 ? 0 : 0.5 }}>
          <Box sx={{ fontWeight: 600 }}>
            {c.name || prettyConnectorType(c.type) || 'Unknown'}
          </Box>
          <Box sx={{ opacity: 0.8 }}>
            {prettyConnectorType(c.type)}
            {c.rows && c.rows.length > 0 ? ` · ${c.rows.slice(0, 3).join(', ')}${c.rows.length > 3 ? `, +${c.rows.length - 3}` : ''}` : ''}
          </Box>
        </Box>
      ))}
    </Box>
  );
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {visible.map((c, i) => (
          <Box
            key={i}
            sx={{
              width: size + 8,
              height: size + 8,
              ml: i === 0 ? 0 : -0.625,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
            }}
          >
            {ConnectorIcon({ type: c.type, size }) || (
              <StorageRounded sx={{ fontSize: size, color: 'text.secondary' }} />
            )}
          </Box>
        ))}
        {overflow > 0 && (
          <Box
            sx={{
              ml: -0.625,
              minWidth: size + 8,
              height: size + 8,
              px: 0.5,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              color: 'text.secondary',
              border: `1px solid ${theme.palette.divider}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6875rem',
              fontWeight: 700,
            }}
          >
            +{overflow}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

// Confidence cell: a 4px bar tinted red/amber/green plus the % numeral.
function ConfidenceBar({ value }: { value: number | null }) {
  const tone = confTone(value);
  const theme = useTheme();
  const colorMap: Record<string, string> = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    muted: theme.palette.divider,
  };
  if (value === null || typeof value !== 'number') {
    return <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>—</Typography>;
  }
  return (
    <Stack spacing={0.5} sx={{ minWidth: 56 }}>
      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: colorMap[tone] }}>
        {fmtPct(value)}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, value * 100))}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: alpha(colorMap[tone], 0.15),
          '& .MuiLinearProgress-bar': { backgroundColor: colorMap[tone] },
        }}
      />
    </Stack>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function EntityDetailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { entityName: entityNameParam } = useParams<{ entityName: string }>();
  const entityName = entityNameParam ? decodeURIComponent(entityNameParam) : '';
  const { notify } = React.useContext(SnackbarContext);

  const [tenantId, setTenantId] = React.useState<string | null>(null);
  // Labels for replicated warehouse tables — used to translate raw normalized
  // table names (e.g. `airtable_appm_…__tbl_…`) into readable leaf labels
  // wherever a source-table reference shows up on this page.
  const { labels: warehouseLabels } = useWarehouseLabels(tenantId);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [allEntities, setAllEntities] = React.useState<any[]>([]);
  const [allRelationships, setAllRelationships] = React.useState<any[]>([]);
  const [canonicalVersion, setCanonicalVersion] = React.useState<number | null>(null);
  const [connectorsMap, setConnectorsMap] = React.useState<Record<string, { name: string; type: string }>>({});

  // Who can see this entity and each of its fields. Empty for anyone who
  // cannot manage roles, and then none of the access controls are drawn.
  const dmAccess = useDataModelAccess();
  const accessRoles = dmAccess.access?.roles ?? [];
  const entityAccess = React.useMemo(
    () => dmAccess.access?.entities.find((e) => e.name === entityName) ?? null,
    [dmAccess.access, entityName],
  );
  const fieldAccessByName = React.useMemo(
    () => new Map((entityAccess?.fields ?? []).map((f) => [f.name, f])),
    [entityAccess],
  );
  const entityAccessOff = !entityAccess
    ? undefined
    : !entityAccess.manageable
      ? 'This entity is not built from a database table, so there is nothing an access rule could hide.'
      : !entityAccess.separable
        ? `Every column here is also used by ${joinNames(entityAccess.shares_tables_with)}, so hiding this entity alone would hide nothing.`
        : undefined;

  /** Roles hidden at the entity, which its fields cannot override. */
  const lockedByEntity = (fieldName?: string): Record<string, string> => {
    const out: Record<string, string> = {};
    const through = fieldName ? fieldAccessByName.get(fieldName)?.visible_through ?? {} : {};
    (entityAccess?.hidden_role_ids ?? []).forEach((id) => {
      out[id] = through[id]?.length
        ? `hidden with ${entityName}, but still visible through ${joinNames(through[id])}, which uses the same column.`
        : `hidden with the whole of ${entityName}. Make the entity visible to change its fields.`;
    });
    return out;
  };

  /** The "Role access" control for one field. */
  const renderFieldAccess = (fieldName: string) => {
    const fa = fieldAccessByName.get(fieldName);
    const warnings = [
      fa?.is_key && 'This is a key field. Questions that join through it will be refused for the roles you hide it from.',
      fa?.shared_with.length && `It will also be hidden in ${joinNames(fa.shared_with)}, which ${fa.shared_with.length === 1 ? 'uses' : 'use'} the same column.`,
    ].filter(Boolean);
    return (
      <VisibleToPicker
        subject={fieldName}
        roles={accessRoles}
        loading={dmAccess.loading}
        hiddenRoleIds={fa?.hidden_role_ids ?? []}
        partialRoleIds={fa?.partial_role_ids ?? []}
        partialNote="Some of its columns are hidden"
        lockedRoles={lockedByEntity(fieldName)}
        hidingWarning={warnings.join(' ') || undefined}
        disabledReason={
          fa && !fa.manageable
            ? 'No database column sits behind this field, so there is nothing an access rule could hide.'
            : undefined
        }
        onApply={(changes) => dmAccess.setFields(entity?.name, [fieldName], changes)}
      />
    );
  };

  // Live entity state — mutated directly, auto-saved to API
  const [entity, setEntity] = React.useState<any | null>(null);
  const [newTagInput, setNewTagInput] = React.useState('');

  // Auto-save status (non-Unassigned only)
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const descTimerRef = React.useRef<any>(null);
  // Refs so debounce/timer callbacks always see the latest values
  const entityRef = React.useRef<any>(null);
  const canonicalVersionRef = React.useRef<number | null>(null);
  React.useEffect(() => { entityRef.current = entity; }, [entity]);
  React.useEffect(() => { canonicalVersionRef.current = canonicalVersion; }, [canonicalVersion]);

  // Force re-render every 30s so "Saved 12s ago" stays accurate.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!lastSavedAt) return;
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  // Field edit modal
  const [fieldModalOpen, setFieldModalOpen] = React.useState(false);
  const [fieldModalIdx, setFieldModalIdx] = React.useState<number | null>(null);
  const [fieldModalData, setFieldModalData] = React.useState<any | null>(null);
  const [fieldModalIsNew, setFieldModalIsNew] = React.useState(false);
  const [fieldModalSaving, setFieldModalSaving] = React.useState(false);

  // Delete-field confirmation
  const [pendingDeleteFieldIdx, setPendingDeleteFieldIdx] = React.useState<number | null>(null);

  // Inline filters (Tier 3)
  const [hideHighConf, setHideHighConf] = React.useState(false);
  const [showOnlyPii, setShowOnlyPii] = React.useState(false);
  const [fieldQuery, setFieldQuery] = React.useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // Pre-select a source table filter when arriving via a deep-link from
  // the Connector Detail page (e.g. clicking an entity chip on a table row).
  const [sourceFilter, setSourceFilter] = React.useState<string>(
    searchParams.get('source') || ''
  );

  // Unassigned entity — keeps explicit Edit / Save / Cancel flow
  const [isEditing, setIsEditing] = React.useState(false);
  const [editData, setEditData] = React.useState<any | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [selectedFieldsBySource, setSelectedFieldsBySource] = React.useState<Record<string, Set<string>>>({});
  const [targetEntityBySource, setTargetEntityBySource] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const tStr = window.localStorage.getItem('klaris_tenant');
    if (!tStr) return;
    try { const t = JSON.parse(tStr); setTenantId(t.tenant_id); } catch {}
  }, []);

  // Load connectors for source tooltip
  React.useEffect(() => {
    if (!tenantId) return;
    const token = window.localStorage.getItem('klaris_jwt');
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list: any[] = Array.isArray(data) ? data : (data.connectors || []);
        const map: Record<string, { name: string; type: string }> = {};
        list.forEach((c: any) => { if (c.connector_id) map[c.connector_id] = { name: c.name || c.type, type: c.type }; });
        setConnectorsMap(map);
      })
      .catch(() => {});
  }, [tenantId]);

  const loadCanonical = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Failed to load data model');
      const result = await resp.json();
      const graph = result?.canonical_graph || {};
      setAllEntities(graph?.entities || []);
      setAllRelationships(graph?.relationships || []);
      setCanonicalVersion(result?.version ?? null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => { loadCanonical(); }, [loadCanonical]);

  React.useEffect(() => {
    const found = allEntities.find((e: any) => e.name === entityName) || null;
    setEntity(found);
    setIsEditing(false);
    setEditData(null);
  }, [allEntities, entityName]);

  const isUnassigned = React.useMemo(
    () => Array.isArray(entity?.tags) && entity.tags.some((t: string) => t.toLowerCase() === 'unassigned'),
    [entity]
  );

  const entityRelationships = React.useMemo(
    () => allRelationships.filter((r: any) => r.from_entity === entityName || r.to_entity === entityName),
    [allRelationships, entityName]
  );

  const targetEntities = React.useMemo(
    () => allEntities
      .filter((e: any) => !Array.isArray(e?.tags) || !e.tags.some((t: string) => t.toLowerCase() === 'unassigned'))
      .map((e: any) => e.name).filter(Boolean),
    [allEntities]
  );

  const mappingsBySource = React.useMemo(() => {
    if (!isUnassigned || !editData) return {};
    const groups: Record<string, Array<{ fieldIdx: number; mappingIdx: number; field: any; mapping: any }>> = {};
    (editData.fields || []).forEach((field: any, fieldIdx: number) => {
      (field.mappings || []).forEach((mapping: any, mappingIdx: number) => {
        const src = mapping.source_entity || 'Unknown';
        if (!groups[src]) groups[src] = [];
        groups[src].push({ fieldIdx, mappingIdx, field, mapping });
      });
    });
    return groups;
  }, [editData, isUnassigned]);

  // Per-field connector summary, deduped by connector. Used in the table cell
  // and in the entity-level sources strip. Raw `source_entity` values are
  // dlt-normalized warehouse table names for replicated connectors — we
  // translate them to friendly leaf labels here so every consumer of this
  // memo (entity-level strip + per-row chips + tooltips) gets the readable
  // form for free.
  const connectorsByField = React.useMemo(() => {
    const out: Record<number, Array<{ connectorId: string; type: string; name: string; rows: string[] }>> = {};
    const fields = entity?.fields || [];
    fields.forEach((field: any, fi: number) => {
      const m = new Map<string, { connectorId: string; type: string; name: string; rows: string[] }>();
      const mappings = Array.isArray(field?.mappings) ? field.mappings : [];
      mappings.forEach((mp: any) => {
        const cid = String(mp?.connector_id || '');
        if (!cid) return;
        const meta = connectorsMap[cid];
        if (!meta) return;
        if (!m.has(cid)) m.set(cid, { connectorId: cid, type: meta.type, name: meta.name, rows: [] });
        const rawTbl = mp?.source_entity ? String(mp.source_entity) : '';
        if (!rawTbl) return;
        const friendly = resolveLabel(rawTbl, warehouseLabels)?.label || rawTbl;
        if (!m.get(cid)!.rows.includes(friendly)) m.get(cid)!.rows.push(friendly);
      });
      out[fi] = Array.from(m.values());
    });
    return out;
  }, [entity, connectorsMap, warehouseLabels]);

  // All distinct source tables across this entity's fields, each with a
  // friendly display label. Used to populate the "Filter by source" selector.
  const allSourceOptions = React.useMemo(() => {
    const seen = new Map<string, string>(); // raw key → friendly label
    (entity?.fields || []).forEach((f: any) => {
      (f?.mappings || []).forEach((m: any) => {
        const raw = String(m?.source_entity || '');
        if (!raw || seen.has(raw)) return;
        const label = resolveLabel(raw, warehouseLabels)?.label || raw;
        seen.set(raw, label);
      });
    });
    return Array.from(seen.entries())
      .map(([raw, label]) => ({ raw, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [entity, warehouseLabels]);

  // Entity-level: union of connectors across all fields.
  const entityConnectors = React.useMemo(() => {
    const m = new Map<string, { connectorId: string; type: string; name: string; rows: string[] }>();
    Object.values(connectorsByField).forEach((arr) => {
      arr.forEach((c) => {
        if (!m.has(c.connectorId)) m.set(c.connectorId, { ...c, rows: [...c.rows] });
        else {
          const ex = m.get(c.connectorId)!;
          c.rows.forEach((r) => { if (!ex.rows.includes(r)) ex.rows.push(r); });
        }
      });
    });
    return Array.from(m.values());
  }, [connectorsByField]);

  // Stats for the strip below the title.
  const stats = React.useMemo(() => {
    const fields = entity?.fields || [];
    const piiCount = fields.filter((f: any) => f?.pii && f.pii !== 'none').length;
    const sources = entityConnectors.length;
    const rels = entityRelationships.length;
    return { fields: fields.length, pii: piiCount, sources, rels };
  }, [entity, entityConnectors, entityRelationships]);

  // Filtered fields based on inline controls.
  const filteredFields = React.useMemo(() => {
    const all = (entity?.fields || []) as any[];
    const q = fieldQuery.trim().toLowerCase();
    return all.filter((f) => {
      if (showOnlyPii && (!f.pii || f.pii === 'none')) return false;
      if (hideHighConf) {
        const c = getFieldConfidence(f);
        if (c !== null && c >= 1) return false;
      }
      if (q) {
        const blob = `${f.name || ''} ${f.description || ''} ${f.semantic_type || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (sourceFilter) {
        const mappings = Array.isArray(f?.mappings) ? f.mappings : [];
        if (!mappings.some((m: any) => m?.source_entity === sourceFilter)) return false;
      }
      return true;
    }).map((f) => ({ field: f, originalIdx: all.indexOf(f) }));
  }, [entity, fieldQuery, hideHighConf, showOnlyPii, sourceFilter]);

  // Suggested tag options for the freeSolo Autocomplete.
  const tagSuggestions = React.useMemo(() => {
    const set = new Set<string>(['core', 'pii', 'transactional', 'reference']);
    allEntities.forEach((e: any) => (e?.tags || []).forEach((t: string) => set.add(String(t))));
    (entity?.tags || []).forEach((t: string) => set.delete(String(t)));
    return Array.from(set);
  }, [allEntities, entity]);

  // ── Auto-save for normal entities ─────────────────────────────────────────
  const saveEntity = async (updatedEntity: any): Promise<boolean> => {
    if (!tenantId) return false;
    setSaveStatus('saving');
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/entity`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_name: updatedEntity.name,
          entity_data: updatedEntity,
          expected_version: canonicalVersionRef.current ?? undefined,
        }),
      });
      const result = await resp.json();
      if (resp.status === 409) {
        setSaveStatus('error');
        notify('Version conflict. Please reload.', 'error');
        return false;
      }
      if (!resp.ok) throw new Error(result?.detail || 'Save failed');
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
      await loadCanonical();
      return true;
    } catch (e: any) {
      setSaveStatus('error');
      notify(e.message || 'Failed to save', 'error');
      return false;
    }
  };

  // Description: debounce 800 ms so we don't save on every keystroke
  const handleDescriptionChange = (value: string) => {
    setEntity((prev: any) => ({ ...prev, description: value }));
    if (descTimerRef.current) clearTimeout(descTimerRef.current);
    descTimerRef.current = setTimeout(() => {
      if (entityRef.current) saveEntity(entityRef.current);
    }, 800);
  };

  const handleAddTag = (tag: string) => {
    const t = String(tag || '').trim();
    if (!t || (entity?.tags || []).includes(t)) return;
    const updated = { ...entity, tags: [...(entity?.tags || []), t] };
    setEntity(updated);
    setNewTagInput('');
    saveEntity(updated);
  };

  const handleSyncTags = (next: string[]) => {
    const updated = { ...entity, tags: next };
    setEntity(updated);
    saveEntity(updated);
  };

  const handleDisplayFieldsChange = (v: string[]) => {
    const updated = { ...entity, display_fields: v };
    setEntity(updated);
    saveEntity(updated);
  };

  const requestDeleteField = (fi: number) => setPendingDeleteFieldIdx(fi);

  const confirmDeleteField = async () => {
    if (pendingDeleteFieldIdx === null) return;
    const fi = pendingDeleteFieldIdx;
    const removedName = entity?.fields?.[fi]?.name || 'Field';
    const updated = { ...entity, fields: entity.fields.filter((_: any, i: number) => i !== fi) };
    setEntity(updated);
    setPendingDeleteFieldIdx(null);
    const ok = await saveEntity(updated);
    if (ok) notify(`Deleted field “${removedName}”`, 'success');
  };

  // ── Field modal ───────────────────────────────────────────────────────────
  const openFieldModal = (fi: number) => {
    const fields = entity?.fields || [];
    setFieldModalIdx(fi);
    setFieldModalData(JSON.parse(JSON.stringify(fields[fi])));
    setFieldModalIsNew(false);
    setFieldModalOpen(true);
  };

  const openNewFieldModal = () => {
    setFieldModalIdx(entity?.fields?.length || 0);
    setFieldModalData({ name: '', description: '', semantic_type: '', pii: 'none', primary_key: false, is_join_key: false, nullable: true, mappings: [] });
    setFieldModalIsNew(true);
    setFieldModalOpen(true);
  };

  const closeFieldModal = () => {
    setFieldModalOpen(false);
    setFieldModalIdx(null);
    setFieldModalData(null);
    setFieldModalIsNew(false);
  };

  const saveFieldModal = async () => {
    if (fieldModalIdx === null || !fieldModalData) return;
    const trimmedName = String(fieldModalData.name || '').trim();
    if (!trimmedName) {
      notify('Field name is required', 'warning');
      return;
    }
    setFieldModalSaving(true);
    const isNew = fieldModalIsNew;
    const fields = [...(entity?.fields || [])];
    if (isNew) {
      fields.push(fieldModalData);
    } else {
      fields[fieldModalIdx] = fieldModalData;
    }
    const updatedEntity = { ...entity, fields };
    setEntity(updatedEntity);
    const ok = await saveEntity(updatedEntity);
    setFieldModalSaving(false);
    if (ok) {
      notify(
        isNew ? `Added field “${trimmedName}”` : `Saved changes to “${trimmedName}”`,
        'success',
      );
      closeFieldModal();
    }
  };

  // ── Unassigned entity: explicit Edit / Save / Cancel ─────────────────────
  const startEdit = () => {
    if (!entity) return;
    setEditData(JSON.parse(JSON.stringify(entity)));
    setNewTagInput('');
    setSelectedFieldsBySource({});
    setTargetEntityBySource({});
    setIsEditing(true);
  };

  const cancelEdit = () => { setIsEditing(false); setEditData(null); };

  const handleSave = async () => {
    if (!tenantId || !editData) return;
    setSaving(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const movements: any[] = [];
      for (const [src, keys] of Object.entries(selectedFieldsBySource)) {
        const target = targetEntityBySource[src];
        if (!target || !keys || (keys as Set<string>).size === 0) continue;
        (keys as Set<string>).forEach((key: string) => {
          const [fi, mi] = key.split('-').map(Number);
          const field = editData.fields[fi];
          const mapping = field?.mappings?.[mi];
          movements.push({
            source_entity_name: editData.name, field_index: fi, mapping_indices: [mi],
            target_entity_name: target, new_field_name: mapping?.source_field || `field_${fi}_${mi}`,
          });
        });
      }
      if (movements.length === 0) { notify('Select fields and target entities to move.', 'warning'); setSaving(false); return; }
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/move-fields`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ movements, expected_version: canonicalVersion ?? undefined }),
      });
      const result = await resp.json();
      if (resp.status === 409) { notify('Version conflict. Please reload and retry.', 'error'); setSaving(false); return; }
      if (!resp.ok) throw new Error(result?.detail || 'Save failed');
      notify(`Moved ${movements.length} field(s) successfully`, 'success');
      setIsEditing(false); setEditData(null);
      await loadCanonical();
    } catch (e: any) {
      notify(e.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const data = (isUnassigned && isEditing) ? editData : entity;

  const entityLoadingSkeleton = (
    <Box sx={{ width: '100%' }}>
      {/* Top bar skeleton */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 2.5,
        pb: 2,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
      }}>
        <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
        <Skeleton variant="rounded" width={220} height={36} sx={{ borderRadius: 1.5 }} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={120} height={26} sx={{ borderRadius: 3 }} />
      </Box>

      {/* Hero with avatar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 1.25 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Skeleton variant="rounded" width={220} height={30} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" width={44} height={22} sx={{ borderRadius: 3 }} />
          </Box>
          <Skeleton variant="rounded" width={420} height={14} sx={{ borderRadius: 1, mb: 1 }} />
          <Skeleton variant="rounded" width={300} height={14} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Two-column grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2.5, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
            <Skeleton variant="rounded" width={80} height={12} sx={{ borderRadius: 1 }} />
          </Box>
          <Box sx={{ px: 2, pt: 1.75, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" width={80} height={11} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width="100%" height={72} sx={{ borderRadius: 1 }} />
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" width={40} height={11} sx={{ borderRadius: 1 }} />
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: 3 }} />
              <Skeleton variant="rounded" width={44} height={22} sx={{ borderRadius: 3 }} />
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: (t) => `1px solid ${t.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="rounded" width={60} height={12} sx={{ borderRadius: 1 }} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="rounded" width={100} height={30} sx={{ borderRadius: 1.5 }} />
          </Box>
          {[...Array(6)].map((_, i) => (
            <Box key={i} sx={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
              gap: 2,
              px: 2,
              py: 1.25,
              borderBottom: (t) => i < 5 ? `1px solid ${t.palette.divider}` : 'none',
              alignItems: 'center',
            }}>
              <Skeleton variant="rounded" width={`${60 + Math.random() * 30}%`} height={13} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width="70%" height={13} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width="55%" height={13} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width="40%" height={13} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Paper>
      </Box>
    </Box>
  );
  if (loading) return entityLoadingSkeleton;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!entity) {
    // The canonical graph has loaded but the effect that resolves `entity`
    // from `allEntities` for the current route hasn't run yet. Keep the
    // shimmer for that one frame instead of flashing "not found".
    const existsInList = allEntities.some((e: any) => e.name === entityName);
    if (existsInList) return entityLoadingSkeleton;
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Entity "{entityName}" not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/data-model')}>Back to Data Model</Button>
      </Box>
    );
  }

  // Pending field-being-deleted (for ConfirmDialog body)
  const pendingDeleteField = pendingDeleteFieldIdx !== null
    ? (entity?.fields || [])[pendingDeleteFieldIdx]
    : null;

  // Render the save chip (used in the top bar, both desktop + mobile).
  const renderSaveChip = () => {
    if (saveStatus === 'saving') {
      return (
        <Chip
          size="small"
          icon={<CircularProgress size={10} sx={{ color: 'text.secondary !important' }} />}
          label="Saving…"
          variant="outlined"
          sx={{ fontSize: '0.75rem', height: 26, color: 'text.secondary', borderColor: 'divider' }}
        />
      );
    }
    if (saveStatus === 'saved') {
      // Soft success treatment via the shared MuiChip-colorSuccess rule
      // (successSoft bg + success fg). Keeps "Saved" chips consistent with
      // every other success badge in the app instead of using a one-off
      // saturated alert-style green.
      return (
        <Chip
          size="small"
          icon={<DoneRounded sx={{ fontSize: '14px !important' }} />}
          label="Saved"
          variant="filled"
          color="success"
          sx={{ fontSize: '0.75rem', height: 26, fontWeight: 600 }}
        />
      );
    }
    if (saveStatus === 'error') {
      return (
        <Chip
          size="small"
          label="Failed to save"
          variant="filled"
          color="error"
          sx={{ fontSize: '0.75rem', height: 26, fontWeight: 600 }}
        />
      );
    }
    if (lastSavedAt) {
      return (
        <Tooltip title={lastSavedAt.toLocaleString()} arrow>
          <Chip
            size="small"
            icon={<DoneRounded sx={{ fontSize: '14px !important' }} />}
            label={`Saved ${formatRelativeTime(lastSavedAt)}`}
            variant="filled"
            color="success"
            sx={{ fontSize: '0.75rem', height: 26, fontWeight: 500 }}
          />
        </Tooltip>
      );
    }
    return (
      <Chip
        size="small"
        icon={<CheckCircleOutlineRounded sx={{ fontSize: '14px !important' }} />}
        label="Auto-saving"
        variant="outlined"
        sx={{ fontSize: '0.75rem', height: 26, color: 'text.disabled', borderColor: 'divider' }}
      />
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Top bar ── */}
      <Box sx={(t) => ({
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1.25, sm: 1.5 },
        mb: 2,
        pb: 1.5,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        flexWrap: 'wrap',
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0, flex: { sm: 1 } }}>
          <Button size="small" startIcon={<ArrowBackRounded />} onClick={() => navigate('/data-model')}
            sx={{ color: 'text.secondary', fontWeight: 500, flexShrink: 0 }}>
            Data Model
          </Button>
          <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', sm: 'inline' } }}>/</Typography>
          <Autocomplete
            size="small"
            options={allEntities.map((e: any) => e.name)}
            value={entityName}
            onChange={(_, val) => val && navigate(`/data-model/entity/${encodeURIComponent(val)}`)}
            disableClearable
            sx={{ width: { xs: '100%', sm: 260 }, minWidth: 0, flex: { xs: '1 1 100%', sm: 'none' } }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Switch entity…"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
            )}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: isUnassigned ? 'space-between' : 'flex-end', sm: 'flex-end' },
          width: { xs: '100%', sm: 'auto' },
          ml: { sm: 'auto' },
        }}>
          {isUnassigned && (
            <Chip
              icon={<InfoOutlined />}
              label={isMobile ? 'Unassigned' : 'Unassigned Fields'}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ maxWidth: { xs: '100%', sm: 'none' } }}
            />
          )}

          {/* Save status indicator — only for normal (non-Unassigned) entities */}
          {!isUnassigned && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
              {renderSaveChip()}
            </Box>
          )}

          {/* Edit / Cancel / Save — only for Unassigned entity */}
          {isUnassigned && (
            <>
              <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
              {!isEditing ? (
                <>
                  <Tooltip title="Edit entity">
                    <IconButton onClick={startEdit} size="small"
                      sx={{ display: { xs: 'inline-flex', sm: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                      aria-label="Edit entity">
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button variant="outlined" size="small" startIcon={<EditRounded />} onClick={startEdit}
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                    Edit
                  </Button>
                </>
              ) : (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' } }}>
                  <Tooltip title="Cancel">
                    <IconButton onClick={cancelEdit} disabled={saving} size="small"
                      sx={{ display: { xs: 'inline-flex', sm: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                      aria-label="Cancel">
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" onClick={cancelEdit} startIcon={<CloseRounded />} disabled={saving}
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                    Cancel
                  </Button>
                  <Tooltip title={saving ? 'Saving…' : 'Save'}>
                    <span>
                      <IconButton color="primary" onClick={handleSave} disabled={saving} size="small"
                        sx={{ display: { xs: 'inline-flex', sm: 'none' } }} aria-label="Save">
                        {saving ? <CircularProgress size={20} color="inherit" /> : <SaveRounded fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button variant="contained" size="small" disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded />}
                    onClick={handleSave}
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Hero: avatar + name + tags + description + stats strip ── */}
      <Box sx={{
        mb: { xs: 2.5, sm: 3 },
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
      }}>
        <EntityAvatar name={entity.name || '?'} size={isMobile ? 40 : 48} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.25 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.2, wordBreak: 'break-word' }}
            >
              {entity.name}
            </Typography>
            {(() => {
              const tags = (entity?.tags || []) as string[];
              const sorted = tags
                .map((t) => ({ raw: t, tone: classifyTag(t) }))
                .sort((a, b) => {
                  const order: Record<TagTone, number> = { pii: 0, core: 1, transactional: 2, reference: 3, neutral: 4, unassigned: 5 };
                  return order[a.tone] - order[b.tone];
                })
                .slice(0, 4);
              const overflow = tags.length - sorted.length;
              return (
                <>
                  {sorted.map(({ raw, tone }, i) => {
                    const props = tagChipProps(tone);
                    return (
                      <Chip
                        key={i}
                        size="small"
                        label={raw}
                        color={props.color}
                        variant={props.variant}
                        icon={props.icon}
                        sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }}
                      />
                    );
                  })}
                  {overflow > 0 && (
                    <Tooltip title={tags.slice(sorted.length).join(', ')} arrow>
                      <Chip size="small" variant="outlined" label={`+${overflow}`} sx={{ fontSize: '0.6875rem', height: 22 }} />
                    </Tooltip>
                  )}
                </>
              );
            })()}
          </Stack>
          {entity.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
              {entity.description}
            </Typography>
          )}

          {/* Stats strip — fields · PII · sources · rels · version */}
          <Stack
            direction="row"
            divider={
              <Box
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  bgcolor: 'text.disabled',
                  alignSelf: 'center',
                  mx: 1,
                }}
              />
            }
            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.fields}</Box>
              {' fields'}
            </Typography>
            {stats.pii > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'warning.main', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <ShieldRounded sx={{ fontSize: 12 }} />
                <Box component="span" sx={{ fontWeight: 700 }}>{stats.pii}</Box>
                {' PII'}
              </Typography>
            )}
            {entityConnectors.length > 0 ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625 }}>
                <SourceIcons connectors={entityConnectors} max={5} size={14} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {entityConnectors.length === 1 ? 'source' : 'sources'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                No sources
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.rels}</Box>
              {' relationships'}
            </Typography>
            {canonicalVersion !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                v{canonicalVersion}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Who can see this entity, in a column of its own. Admins only. */}
        {dmAccess.canManage && (dmAccess.loading || entityAccess) && (
          <Box sx={(t) => ({
            width: { xs: '100%', md: 260 },
            flexShrink: 0,
            alignSelf: 'stretch',
            pl: { md: 2.5 },
            pt: { xs: 1.5, md: 0 },
            borderLeft: { md: `1px solid ${t.tokens.border.subtle}` },
            borderTop: { xs: `1px solid ${t.tokens.border.subtle}`, md: 'none' },
          })}>
            <Typography variant="overline" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              Role access
            </Typography>
            <VisibleToPicker
              variant="select"
              subject={entity.name}
              roles={accessRoles}
              loading={dmAccess.loading}
              hiddenRoleIds={entityAccess?.hidden_role_ids ?? []}
              partialRoleIds={entityAccess?.partial_role_ids ?? []}
              partialNote="Some fields are hidden"
              disabledReason={entityAccessOff}
              hidingWarning={
                entityAccess?.shares_tables_with.length
                  ? `${entity.name} shares a table with ${joinNames(entityAccess.shares_tables_with)}. Only the fields that are ${entity.name}'s alone will be hidden; the shared ones stay visible through ${joinNames(entityAccess.shares_tables_with)}.`
                  : `Every field of ${entity.name} will be hidden from the roles you untick, in chat and anywhere else a question is asked of the data.`
              }
              showingWarnings={Object.fromEntries(
                Object.entries(entityAccess?.remembered_fields ?? {}).map(([roleId, names]) => {
                  const role = accessRoles.find((r) => r.role_id === roleId)?.name ?? 'this role';
                  return [
                    roleId,
                    `This will also make ${names.length} hidden ${names.length === 1 ? 'field' : 'fields'} visible to ${role}: ${joinNames(names)}.`,
                  ];
                }),
              )}
              onApply={(changes) => dmAccess.setEntity(entity.name, changes)}
            />

            {entityAccessOff && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
                {entityAccessOff}
              </Typography>
            )}
            {dmAccess.access && !dmAccess.access.enforced && (
              <Typography variant="caption" component="div" sx={{ mt: 0.75, color: 'warning.dark' }}>
                Access rules are switched off for this workspace, so what you choose here is saved
                but not applied yet. Switch on Role-Based Table Access under Settings, Features.
              </Typography>
            )}
            {dmAccess.error && (
              <Typography variant="caption" component="div" color="error" sx={{ mt: 0.75 }}>
                {dmAccess.error}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* ── Main content ── */}
      {isUnassigned && isEditing ? (
        <UnassignedEditView
          editData={editData} setEditData={setEditData}
          mappingsBySource={mappingsBySource} targetEntities={targetEntities}
          selectedFieldsBySource={selectedFieldsBySource} setSelectedFieldsBySource={setSelectedFieldsBySource}
          targetEntityBySource={targetEntityBySource} setTargetEntityBySource={setTargetEntityBySource}
          warehouseLabels={warehouseLabels}
        />
      ) : (
        <Stack spacing={2.5}>
          {/* ── Two-column: info + fields ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2.5, alignItems: 'start' }}>

            {/* Left: Entity Info card — always editable for normal entities */}
            <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle })}>
              <Box sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                bgcolor: theme.tokens.surface.sunken,
                borderBottom: `1px solid ${theme.tokens.border.subtle}`,
              }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                  Entity info
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.75, pb: 1.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}>
                  Description
                </Typography>
                <TextField fullWidth multiline minRows={3} size="small"
                  value={entity?.description || ''}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  placeholder="Describe this entity…"
                />
              </Box>

              <Divider />

              {/* Tags — Autocomplete freeSolo with suggestions */}
              <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 1.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}>
                  Tags
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={tagSuggestions}
                  value={(entity?.tags || []) as string[]}
                  onChange={(_, val) => handleSyncTags((val as string[]).map((s) => s.trim()).filter(Boolean))}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => {
                      const tone = classifyTag(opt);
                      const props = tagChipProps(tone);
                      const tagProps = getTagProps({ index: i });
                      return (
                        <Chip
                          {...tagProps}
                          key={tagProps.key}
                          label={opt}
                          size="small"
                          color={props.color}
                          variant={props.variant}
                          icon={props.icon}
                          sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }}
                        />
                      );
                    })
                  }
                  inputValue={newTagInput}
                  onInputChange={(_, v, reason) => {
                    if (reason !== 'reset') setNewTagInput(v);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Add tag…" />
                  )}
                />
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, fontSize: '0.6875rem' }}>
                  Try: core, pii, reference, transactional
                </Typography>
              </Box>

              <Divider />

              {/* Display Fields — clearer copy, primary chip stays primary */}
              <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 2 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary"
                  sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                  Display fields
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 1, fontSize: '0.75rem' }}>
                  These are what users see when this entity appears in answers. The first chip is the primary label — drag to reorder.
                </Typography>
                <Autocomplete
                  multiple size="small"
                  options={(entity?.fields || []).map((f: any) => f.name).filter(Boolean)}
                  value={entity?.display_fields || []}
                  onChange={(_, v) => handleDisplayFieldsChange(v)}
                  renderInput={(params) => <TextField {...params} placeholder="Pick fields…" />}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => {
                      const tagProps = getTagProps({ index: i });
                      return (
                        <Chip
                          {...tagProps}
                          key={tagProps.key}
                          label={i === 0 ? `${opt} · primary` : opt}
                          size="small"
                          color={i === 0 ? 'primary' : 'default'}
                          variant={i === 0 ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }}
                        />
                      );
                    })
                  }
                />
              </Box>
            </Paper>

            {/* Right: Fields */}
            <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle })}>
              {/* Header with toolbar (search + filters + add) */}
              <Box sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                borderBottom: `1px solid ${theme.tokens.border.subtle}`,
                bgcolor: theme.tokens.surface.sunken,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 1 },
              }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', flexShrink: 0 }}>
                  Fields ({(data?.fields || []).length})
                </Typography>
                <Box sx={{ flex: 1 }} />
                {(data?.fields || []).length > 0 && (
                  <>
                    <TextField
                      size="small"
                      value={fieldQuery}
                      onChange={(e) => setFieldQuery(e.target.value)}
                      placeholder="Search fields…"
                      sx={{
                        width: { xs: '100%', sm: 200 },
                        '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8125rem' },
                      }}
                    />
                    {allSourceOptions.length > 0 && (
                      <Select
                        size="small"
                        displayEmpty
                        value={sourceFilter}
                        // Disable (but still show) when there's only one source so
                        // the user can see where the fields come from even if filtering
                        // is not meaningful yet.
                        disabled={allSourceOptions.length === 1 && !sourceFilter}
                        onChange={(e) => {
                          const val = e.target.value as string;
                          setSourceFilter(val);
                          // Keep the URL param in sync so refreshing preserves the filter
                          if (val) setSearchParams({ source: val });
                          else setSearchParams({});
                        }}
                        renderValue={(v) => v
                          ? <Stack direction="row" alignItems="center" spacing={0.5}>
                              <FilterListRounded sx={{ fontSize: 14 }} />
                              <span>{allSourceOptions.find((o) => o.raw === v)?.label ?? v}</span>
                            </Stack>
                          : allSourceOptions.length === 1
                            ? <Stack direction="row" alignItems="center" spacing={0.5}>
                                <FilterListRounded sx={{ fontSize: 14 }} />
                                <span>{allSourceOptions[0].label}</span>
                              </Stack>
                            : <Stack direction="row" alignItems="center" spacing={0.5}>
                                <FilterListRounded sx={{ fontSize: 14 }} />
                                <span>All sources</span>
                              </Stack>
                        }
                        sx={{
                          fontSize: '0.8125rem',
                          minWidth: 140,
                          borderRadius: 1.5,
                          color: sourceFilter ? 'primary.main' : 'text.secondary',
                          '& .MuiOutlinedInput-notchedOutline': sourceFilter
                            ? { borderColor: 'primary.main' }
                            : {},
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All sources</MenuItem>
                        <Divider />
                        {allSourceOptions.map((opt) => (
                          <MenuItem key={opt.raw} value={opt.raw} sx={{ fontSize: '0.8125rem' }}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    <ToggleButtonGroup
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 0.5,
                          px: 1,
                          gap: 0.5,
                        },
                      }}
                    >
                      <ToggleButton
                        value="pii"
                        selected={showOnlyPii}
                        onClick={() => setShowOnlyPii((v) => !v)}
                        aria-label="Show only PII"
                      >
                        <ShieldRounded sx={{ fontSize: 14 }} /> PII only
                      </ToggleButton>
                      <ToggleButton
                        value="hideHigh"
                        selected={hideHighConf}
                        onClick={() => setHideHighConf((v) => !v)}
                        aria-label="Hide high-confidence rows"
                      >
                        Needs review
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={openNewFieldModal}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, textTransform: 'none', fontWeight: 600 }}
                >
                  Add field
                </Button>
              </Box>

              {/* Empty state — page level (no fields at all) */}
              {(data?.fields || []).length === 0 ? (
                <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
                  <Box sx={{
                    width: 56, height: 56, mx: 'auto', mb: 1.5,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <VpnKeyOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                    No fields yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 360, mx: 'auto' }}>
                    Add the columns this entity exposes. You can map each field to source connectors so AI answers know where to look.
                  </Typography>
                  <Button variant="contained" size="small" startIcon={<AddRounded />} onClick={openNewFieldModal}>
                    Add the first field
                  </Button>
                </Box>
              ) : isMobile ? (
                /* Mobile: card list — table is unusable on small screens */
                <Box>
                  {filteredFields.length === 0 ? (
                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No fields match your filters.
                      </Typography>
                      <Button size="small" sx={{ mt: 1 }} onClick={() => { setFieldQuery(''); setShowOnlyPii(false); setHideHighConf(false); setSourceFilter(''); setSearchParams({}); }}>
                        Clear filters
                      </Button>
                    </Box>
                  ) : (
                    filteredFields.map(({ field, originalIdx }) => {
                      const conf = (() => {
                        const ms = Array.isArray(field.mappings) ? field.mappings : [];
                        if (ms.length > 0) return ms.reduce((a: number, m: any) => a + (m.confidence || 0), 0) / ms.length;
                        return getFieldConfidence(field);
                      })();
                      const conns = connectorsByField[originalIdx] || [];
                      return (
                        <Box
                          key={originalIdx}
                          onClick={() => openFieldModal(originalIdx)}
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${theme.tokens.border.subtle}`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:last-of-type': { borderBottom: 'none' },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                              {field.name}
                            </Typography>
                            {(field.pii && field.pii !== 'none') && (
                              <Chip size="small" label={field.pii} variant="outlined"
                                color={field.pii === 'high' ? 'error' : 'warning'}
                                sx={{ fontSize: '0.6875rem', height: 20, fontWeight: 600 }} />
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => { e.stopPropagation(); requestDeleteField(originalIdx); }}
                              aria-label="Delete field"
                            >
                              <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Stack>
                          {field.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}>
                              {field.description}
                            </Typography>
                          )}
                          {dmAccess.canManage && (
                            <Stack direction="row" spacing={1} alignItems="center"
                              onClick={(e) => e.stopPropagation()} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Role access</Typography>
                              {renderFieldAccess(field.name)}
                            </Stack>
                          )}
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                            {field.semantic_type && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {field.semantic_type}
                              </Typography>
                            )}
                            <Box sx={{ minWidth: 64 }}>
                              <ConfidenceBar value={conf} />
                            </Box>
                            <SourceIcons connectors={conns} size={14} max={3} />
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                </Box>
              ) : (
                /* Desktop: table */
                <Box sx={{ maxHeight: 520, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, minWidth: 140, bgcolor: theme.tokens.surface.sunken }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 180, bgcolor: theme.tokens.surface.sunken }}>Description</TableCell>
                        {dmAccess.canManage && (
                          <TableCell sx={{ fontWeight: 600, minWidth: 200, bgcolor: theme.tokens.surface.sunken }}>Role access</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600, width: 120, bgcolor: theme.tokens.surface.sunken }}>Semantic</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 80, bgcolor: theme.tokens.surface.sunken }}>PII</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 92, bgcolor: theme.tokens.surface.sunken }}>Confidence</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 96, bgcolor: theme.tokens.surface.sunken }}>Sources</TableCell>
                        <TableCell sx={{ width: 40, bgcolor: theme.tokens.surface.sunken }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFields.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={dmAccess.canManage ? 8 : 7} sx={{ py: 4 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No fields match your filters.
                              </Typography>
                              <Button size="small" onClick={() => { setFieldQuery(''); setShowOnlyPii(false); setHideHighConf(false); setSourceFilter(''); setSearchParams({}); }}>
                                Clear filters
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredFields.map(({ field, originalIdx }) => {
                          const mappings = Array.isArray(field.mappings) ? field.mappings : [];
                          const conf = mappings.length > 0
                            ? mappings.reduce((acc: number, m: any) => acc + (m.confidence || 0), 0) / mappings.length
                            : getFieldConfidence(field);
                          const conns = connectorsByField[originalIdx] || [];

                          return (
                            <TableRow key={originalIdx}
                              onClick={() => openFieldModal(originalIdx)}
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  {field.primary_key && (
                                    <Tooltip title="Primary key" arrow>
                                      <VpnKeyOutlined sx={{ fontSize: 13, color: 'warning.main' }} />
                                    </Tooltip>
                                  )}
                                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8125rem' }}>
                                    {field.name}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary"
                                  sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}
                                  title={field.description}>
                                  {field.description || '—'}
                                </Typography>
                              </TableCell>
                              {dmAccess.canManage && (
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {renderFieldAccess(field.name)}
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                  {field.semantic_type || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {(field.pii && field.pii !== 'none') ? (
                                  <Chip size="small" label={field.pii} variant="outlined"
                                    color={field.pii === 'high' ? 'error' : 'warning'}
                                    sx={{ fontSize: '0.6875rem', height: 20, fontWeight: 600 }} />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <ConfidenceBar value={conf} />
                              </TableCell>
                              <TableCell>
                                <SourceIcons connectors={conns} size={14} max={3} />
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="Delete field" arrow>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => requestDeleteField(originalIdx)}
                                  >
                                    <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </Box>

          {/* ── Relationships — full width below ── */}
          <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle })}>
            <Box sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              borderBottom: `1px solid ${theme.tokens.border.subtle}`,
              bgcolor: theme.tokens.surface.sunken,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                Relationships ({entityRelationships.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* Header link only shows once relationships exist — in the
                  empty state the centered "Add" CTA below is the single,
                  unambiguous primary action and a duplicate "Manage"
                  link here would just split the user's attention. */}
              {entityRelationships.length > 0 && (
                <Button
                  size="small"
                  startIcon={<HubOutlined sx={{ fontSize: 14 }} />}
                  endIcon={<OpenInNewRounded sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/data-model')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Manage in Data Model
                </Button>
              )}
            </Box>
            {entityRelationships.length === 0 ? (
              <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                <Box sx={{
                  width: 56, height: 56, mx: 'auto', mb: 1.5,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <HubOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  No relationships yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 380, mx: 'auto' }}>
                  Tell the model how this entity links to others — e.g. <em>Customer → Order</em> on <code>customer_id</code>. Better joins mean better answers.
                </Typography>
                {/* Deep-links into the Data Model page with a query param
                    that asks it to open the Add Relationship modal pre-
                    filled with the current entity as `from_entity`. Avoids
                    dropping the user onto the data-model list page with no
                    context (the previous behaviour) and removes the need
                    for the duplicate "Manage" link in this empty state. */}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={() => navigate(
                    `/data-model?addRelationshipFor=${encodeURIComponent(entityName)}`,
                  )}
                >
                  Add a relationship
                </Button>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Table size="small" sx={{ minWidth: isMobile ? 560 : undefined }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.tokens.surface.canvas }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Connection</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Join on</TableCell>
                      {(() => {
                        const allHaveSameConf = entityRelationships.every(
                          (r: any) => typeof r.confidence === 'number' && r.confidence === entityRelationships[0].confidence
                        );
                        if (allHaveSameConf) return null;
                        return <TableCell sx={{ fontWeight: 600, width: 96 }}>Confidence</TableCell>;
                      })()}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const allHaveSameConf = entityRelationships.every(
                        (r: any) => typeof r.confidence === 'number' && r.confidence === entityRelationships[0].confidence
                      );
                      return entityRelationships.map((rel: any, i: number) => {
                        const joinText = Array.isArray(rel.join_on)
                          ? rel.join_on.map((j: any) => `${j.from_field} = ${j.to_field}`).join(', ')
                          : '';
                        const fromIsThis = rel.from_entity === entityName;
                        return (
                          <TableRow key={i} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={fromIsThis ? 700 : 500}
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: fromIsThis ? 'text.primary' : 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                  }}
                                  title={rel.from_entity}
                                >
                                  {rel.from_entity}
                                </Typography>
                                <ArrowForwardRounded sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                                <Typography
                                  variant="body2"
                                  fontWeight={!fromIsThis ? 700 : 500}
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: !fromIsThis ? 'text.primary' : 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                  }}
                                  title={rel.to_entity}
                                >
                                  {rel.to_entity}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={rel.type} variant="outlined"
                                sx={{ fontSize: '0.6875rem', height: 20 }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {joinText || '—'}
                              </Typography>
                            </TableCell>
                            {!allHaveSameConf && (
                              <TableCell>
                                <ConfidenceBar value={typeof rel.confidence === 'number' ? rel.confidence : null} />
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Stack>
      )}

      {/* ── Field modal ── */}
      <AppDialog
        open={fieldModalOpen}
        onClose={closeFieldModal}
        size="sm"
        title={fieldModalIsNew ? 'New field' : 'Edit field'}
        subtitle={fieldModalIsNew
          ? 'Define a column on this entity. Source mappings can be added later.'
          : 'Edit the metadata users (and the AI) see for this field.'}
        busy={fieldModalSaving}
        dividers
        primaryAction={{
          label: fieldModalSaving ? 'Saving…' : (fieldModalIsNew ? 'Add field' : 'Save changes'),
          onClick: saveFieldModal,
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: closeFieldModal }}
      >
        {fieldModalData && (
          <Stack spacing={3} sx={{ pt: 0.5 }}>
            {/* Identity */}
            <Stack spacing={2}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}
              >
                Identity
              </Typography>
              <TextField
                label="Field name"
                fullWidth
                value={fieldModalData.name || ''}
                onChange={(e) => setFieldModalData({ ...fieldModalData, name: e.target.value })}
                autoFocus
                helperText="Use snake_case so it's easy to reference (e.g. customer_id)."
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={fieldModalData.description || ''}
                onChange={(e) => setFieldModalData({ ...fieldModalData, description: e.target.value })}
                placeholder="What does this field represent?"
                helperText="Plain English. The AI uses this to understand intent."
              />
              <TextField
                label="Semantic type"
                fullWidth
                value={fieldModalData.semantic_type || ''}
                onChange={(e) => setFieldModalData({ ...fieldModalData, semantic_type: e.target.value })}
                placeholder="e.g. email, currency, date, id"
                helperText="A high-level role for this value. Helps the AI pick the right operation."
              />
            </Stack>

            <Divider />

            {/* Sensitivity */}
            <Stack spacing={1}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}
              >
                Sensitivity
              </Typography>
              <RadioGroup
                row
                value={fieldModalData.pii || 'none'}
                onChange={(e) => setFieldModalData({ ...fieldModalData, pii: e.target.value })}
                sx={{ gap: 1, flexWrap: 'wrap' }}
              >
                {[
                  { value: 'none', label: 'None', sub: 'Not sensitive' },
                  { value: 'low', label: 'Low', sub: 'Internal use' },
                  { value: 'medium', label: 'Medium', sub: 'Restricted' },
                  { value: 'high', label: 'High', sub: 'PII / regulated' },
                ].map((opt) => {
                  const selected = (fieldModalData.pii || 'none') === opt.value;
                  const tone =
                    opt.value === 'high' ? 'error' :
                    opt.value === 'medium' ? 'warning' :
                    opt.value === 'low' ? 'info' : 'inherit';
                  const toneColor = (tone === 'inherit' ? theme.palette.text.secondary : (theme.palette as any)[tone].main);
                  return (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio sx={{ display: 'none' }} />}
                      sx={{ m: 0 }}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            px: 1.25,
                            py: 0.875,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: selected ? toneColor : theme.palette.divider,
                            bgcolor: selected ? alpha(toneColor, 0.08) : 'transparent',
                            cursor: 'pointer',
                            minWidth: 96,
                            transition: 'border-color .15s ease, background-color .15s ease',
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ fontSize: '0.75rem', color: selected ? toneColor : 'text.primary' }}
                          >
                            {opt.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.6875rem' }}
                          >
                            {opt.sub}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Stack>
          </Stack>
        )}
      </AppDialog>

      {/* ── Delete-field confirmation ── */}
      <ConfirmDialog
        open={pendingDeleteFieldIdx !== null}
        onClose={() => setPendingDeleteFieldIdx(null)}
        onConfirm={confirmDeleteField}
        tone="destructive"
        title={pendingDeleteField?.name ? `Delete field “${pendingDeleteField.name}”?` : 'Delete field?'}
        description="This removes the field from the entity and any of its source mappings. Existing AI answers and dashboards that reference it may break."
        confirmLabel="Delete field"
      />
    </Box>
  );
}

// ── Unassigned entity batch-assign view ───────────────────────────────────────
interface UnassignedEditViewProps {
  editData: any;
  setEditData: (d: any) => void;
  mappingsBySource: Record<string, Array<{ fieldIdx: number; mappingIdx: number; field: any; mapping: any }>>;
  targetEntities: string[];
  selectedFieldsBySource: Record<string, Set<string>>;
  setSelectedFieldsBySource: React.Dispatch<React.SetStateAction<Record<string, Set<string>>>>;
  targetEntityBySource: Record<string, string>;
  setTargetEntityBySource: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  /** Map for translating raw normalized source-table names into friendly labels. */
  warehouseLabels?: WarehouseLabelMap;
}

function UnassignedEditView({
  editData, mappingsBySource, targetEntities,
  selectedFieldsBySource, setSelectedFieldsBySource,
  targetEntityBySource, setTargetEntityBySource,
  warehouseLabels,
}: UnassignedEditViewProps) {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2.5 }} icon={<InfoOutlined />}>
        <strong>Batch assign fields by source:</strong> Fields are grouped by their source table.
        Select fields from each source, choose a target entity, then click Save.
      </Alert>
      <Stack spacing={2}>
        {Object.entries(mappingsBySource).sort(([a], [b]) => a.localeCompare(b)).map(([src, items]) => {
          const selectedKeys = selectedFieldsBySource[src] || new Set<string>();
          const allKeys = items.map(it => `${it.fieldIdx}-${it.mappingIdx}`);
          const allSelected = allKeys.length > 0 && allKeys.every(k => selectedKeys.has(k));
          const someSelected = allKeys.some(k => selectedKeys.has(k));
          const targetEntity = targetEntityBySource[src];
          return (
            <Paper key={src} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Checkbox checked={allSelected} indeterminate={someSelected && !allSelected}
                    onChange={(e) => {
                      const next = new Set(selectedKeys);
                      if (e.target.checked) allKeys.forEach(k => next.add(k));
                      else allKeys.forEach(k => next.delete(k));
                      setSelectedFieldsBySource(prev => ({ ...prev, [src]: next }));
                    }}
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {(() => {
                      // `src` is the raw source-table identifier and stays the
                      // state key (selectedFieldsBySource[src] etc.). For
                      // display we resolve the friendly breadcrumb when the
                      // source is a replicated warehouse table.
                      const entry = warehouseLabels ? resolveLabel(src, warehouseLabels) : null;
                      const heading = entry?.label || src;
                      const subPath = entry && entry.breadcrumb.length > 1
                        ? entry.breadcrumb.slice(0, -1).join(' \u203A ')
                        : '';
                      return (
                        <>
                          {subPath && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                              {subPath}
                            </Typography>
                          )}
                          <Typography variant="subtitle2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{heading}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {items.length} field{items.length !== 1 ? 's' : ''}
                            {selectedKeys.size > 0 ? ` \u00b7 ${selectedKeys.size} selected` : ''}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                  <Autocomplete size="small" options={targetEntities}
                    value={targetEntity || null}
                    onChange={(_, v) => setTargetEntityBySource(prev => ({ ...prev, [src]: v || '' }))}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Move to entity…"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: targetEntity
                            ? <ArrowForwardRounded fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                            : null,
                        }}
                      />
                    )}
                    sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}
                  />
                </Stack>
                <Box sx={{ pl: { xs: 0, sm: 5 }, pt: { xs: 0.5, sm: 0 } }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(140px, 1fr))', sm: 'repeat(auto-fill, minmax(160px, 1fr))' }, gap: 0.5 }}>
                    {items.map((item) => {
                      const key = `${item.fieldIdx}-${item.mappingIdx}`;
                      const isSelected = selectedKeys.has(key);
                      return (
                        <Box key={key}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            py: 0.4, px: 0.75, borderRadius: 1,
                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            const next = new Set(selectedKeys);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            setSelectedFieldsBySource(prev => ({ ...prev, [src]: next }));
                          }}
                        >
                          <Checkbox size="small" checked={isSelected} sx={{ p: 0 }} onChange={() => {}} />
                          <Typography variant="body2"
                            sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={item.mapping.source_field}>
                            {item.mapping.source_field || '—'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Paper>
          );
        })}
        {Object.keys(mappingsBySource).length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No unassigned fields.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
